import { gql } from '@apollo/client';

export const GET_TREATMENT_REVIEW_LIST = gql`
  query getTreatmentReviewList($patientId: ID!) {
    treatmentReviewList(patientId: $patientId) {
      id
      reviewDate
      roPlan
      toxicityType
      toxicityCategories
      otherToxicityDetails
      doseSiteSummariesLastSyncedAt
      dosesitesummarySet {
        id
        isShown
        isSelected
        emrType
        course
        site
        technique
        status
        statusReason
        treatmentStartDate
        treatmentEndDate
        treatmentLatestDate
        prescribedDosage
        prescribedFractionDose
        deliveredDosage
        deliveredFractionDose
        group
      }
      status
      submittedAt
      lastSubmittedAt
      additionalSummaryReviewNotes
      group {
        id
      }
    }
  }
`;

export const CREATE_TREATMENT_REVIEW = gql`
  mutation createTreatmentReview($patientId: ID!) {
    createTreatmentReview(patientId: $patientId) {
      review {
        id
        group {
          id
        }
      }
    }
  }
`;

export const UPDATE_TREATMENT_REVIEW = gql`
  mutation updateTreatmentReview(
    $groupId: ID!
    $reviewDate: Date
    $roPlan: String
    $toxicityType: String
    $toxicityCategories: [ID]
    $otherToxicityDetails: String
    $additionalSummaryReviewNotes: String
  ) {
    updateTreatmentReview(
      groupId: $groupId
      reviewDate: $reviewDate
      roPlan: $roPlan
      toxicityType: $toxicityType
      toxicityCategories: $toxicityCategories
      otherToxicityDetails: $otherToxicityDetails
      additionalSummaryReviewNotes: $additionalSummaryReviewNotes
    ) {
      review {
        id
        currentChanges
        group {
          id
        }
      }
    }
  }
`;

export const SUBMIT_TREATMENT_REVIEW = gql`
  mutation submitTreatmentReview($groupId: ID!) {
    submitTreatmentReview(groupId: $groupId) {
      review {
        id
        reviewDate
        roPlan
        toxicityType
        toxicityCategories
        otherToxicityDetails
        additionalSummaryReviewNotes
        dosesitesummarySet {
          id
          isShown
          isSelected
          emrType
          course
          site
          technique
          status
          statusReason
          treatmentStartDate
          treatmentEndDate
          treatmentLatestDate
          prescribedDosage
          prescribedFractionDose
          deliveredDosage
          deliveredFractionDose
          group
        }
        status
        submittedAt
        lastSubmittedAt
      }
    }
  }
`;

export const DISCARD_TREATMENT_REVIEW = gql`
  mutation discardTreatmentReview($groupId: ID!) {
    discardTreatmentReview(groupId: $groupId) {
      review {
        id
        reviewDate
        roPlan
        toxicityType
        toxicityCategories
        otherToxicityDetails
        additionalSummaryReviewNotes
        dosesitesummarySet {
          id
          isShown
          isSelected
          emrType
          course
          site
          technique
          status
          statusReason
          treatmentStartDate
          treatmentEndDate
          treatmentLatestDate
          prescribedDosage
          prescribedFractionDose
          deliveredDosage
          deliveredFractionDose
          group
        }
        doseSiteSummariesLastSyncedAt
        status
        submittedAt
        lastSubmittedAt
        currentChanges
      }
    }
  }
`;

export const GET_TREATMENT_REVIEW = gql`
  query getTreatmentReview($groupId: ID!) {
    treatmentReview(groupId: $groupId) {
      id
      reviewDate
      roPlan
      toxicityType
      toxicityCategories
      otherToxicityDetails
      dosesitesummarySet {
        id
        isShown
        isSelected
        emrType
        course
        site
        technique
        status
        statusReason
        treatmentStartDate
        treatmentEndDate
        treatmentLatestDate
        prescribedDosage
        prescribedFractionDose
        deliveredDosage
        deliveredFractionDose
        group
      }
      doseSiteSummariesLastSyncedAt
      additionalSummaryReviewNotes
      status
      submittedAt
      lastSubmittedAt
      currentChanges
      practitioner {
        id
        name
      }
      group {
        id
      }
    }
  }
`;

export const DELETE_TREATMENT_REVIEW = gql`
  mutation deleteTreatmentReview($groupId: ID!) {
    deleteTreatmentReview(groupId: $groupId) {
      success
    }
  }
`;

export const REFRESH_DOSE_SITE_SUMMARIES = gql`
  mutation refreshDoseSiteSummaries($groupId: ID!) {
    refreshDoseSiteSummaries(groupId: $groupId) {
      review {
        id
        doseSiteSummariesLastSyncedAt
      }
    }
  }
`;

export const UPDATE_DOSE_SITE_SUMMARY = gql`
  mutation updateDoseSiteSummary($id: ID!, $isSelected: Boolean!) {
    updateDoseSiteSummary(id: $id, isSelected: $isSelected) {
      review {
        id
        dosesitesummarySet {
          id
          isShown
          isSelected
          emrType
          course
          site
          technique
          status
          statusReason
          treatmentStartDate
          treatmentEndDate
          treatmentLatestDate
          prescribedDosage
          prescribedFractionDose
          deliveredDosage
          deliveredFractionDose
          group
        }
      }
    }
  }
`;

export const GET_SYMPTOM_CATEGORIES = gql`
  query getToxicityCategories {
    toxicityCategories(active: true) {
      id
      name
    }
  }
`;

export const GET_SYMPTOM_CATEGORIES_AND_SYMPTOMS = gql`
  query getToxicityCategoriesAndSymptoms($active: Boolean) {
    toxicityCategories(active: $active) {
      id
      name
    }
    toxicitySymptoms(active: $active) {
      id
      name
      categoryIds
      grade1Description
      grade2Description
      grade3Description
      grade4Description
      grade5Description
    }
  }
`;

export const GET_TREATMENT_REVIEW_SYMPTOMS = gql`
  query getTreatmentReviewSymptoms($groupId: ID!) {
    treatmentReviewSymptoms(groupId: $groupId) {
      id
      grade
      manuallyAdded
      createdAt
      symptom {
        id
        name
      }
    }
  }
`;

export const UPSERT_TREATMENT_REVIEW_SYMPTOM = gql`
  mutation upsertTreatmentReviewSymptom($groupId: ID!, $symptomId: ID!, $grade: Int) {
    upsertTreatmentReviewSymptom(groupId: $groupId, symptomId: $symptomId, grade: $grade) {
      reviewSymptom {
        id
        grade
        manuallyAdded
        symptom {
          id
          name
        }
      }
    }
  }
`;

export const DELETE_TREATMENT_REVIEW_SYMPTOM = gql`
  mutation deleteTreatmentReviewSymptom($groupId: ID!, $symptomId: ID!) {
    deleteTreatmentReviewSymptom(groupId: $groupId, symptomId: $symptomId) {
      success
    }
  }
`;
